import {
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import type {
  IxcCorpactionsV1CorpactionTransaction,
  IxcCorpactionsV1CustomerAction,
  IxcCorpactionsV1CustomerActionSecurityDeltas,
  IxcCorpactionsV1CustomerActionSecurityDeltasCashInLieu,
  IxcCorpactionsV1CustomerCashDelta,
} from '@interxfi/types';
import { getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getAssetDetailPath } from '../../asset/asset-routing';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getCorpactCaAdminActionDetailPath } from '../ca-admin/action/ca-admin-action-routing';
import {
  getCorpactCostCalculationTypeListItems,
  getCorpactCustomerAssignmentListItems,
  getCorpactHeaderListItems,
  getCorpactRoundingRuleListItems,
} from '../corpact-shared-list-items';

export function getCorpactCustomerActionSecurityDeltaCashInLieuListItems(
  data: IxcCorpactionsV1CustomerActionSecurityDeltasCashInLieu | undefined,
  prefix = '',
) {
  return {
    status: getReadonlyListItemTranslatedString(`${prefix}status`, 'Status', {
      namespace: 'ixcCorpactionsV1',
      enumName: 'IxcCorpactionsV1DeferredStatus',
      value: data?.status,
    }),
    rate: getReadonlyListItemNumber(`${prefix}rate`, 'Rate', data?.rate),
    timeApplicable: getReadonlyListItemDate(`${prefix}timeApplicable`, 'Time Applicable', data?.timeApplicable, 'timestamp_iso_ms'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCustomerActionSecurityDeltaListItems(data: IxcCorpactionsV1CustomerActionSecurityDeltas | undefined, prefix = '') {
  return {
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    rate: getReadonlyListItemNumber(`${prefix}rate`, 'Rate', data?.rate),
    costCalculation: data?.costCalculation ? getCorpactCostCalculationTypeListItems(data.costCalculation, `${prefix}costCalculation.`) : undefined,
    rounding: getCorpactRoundingRuleListItems(data?.rounding, `${prefix}rounding.`),
    cashInLieu: data?.cashInLieu ? getCorpactCustomerActionSecurityDeltaCashInLieuListItems(data.cashInLieu, `${prefix}cashInLieu.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCustomerActionCashDeltaListItems(data: IxcCorpactionsV1CustomerCashDelta | undefined, prefix = '') {
  return {
    rate: getReadonlyListItemNumber(`${prefix}rate`, 'Rate', data?.rate),
    costCalculation: data?.costCalculation ? getCorpactCostCalculationTypeListItems(data.costCalculation, `${prefix}costCalculation.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCustomerActionListItems(data: IxcCorpactionsV1CustomerAction | undefined, prefix = '') {
  return {
    header: getCorpactHeaderListItems(data?.header, `${prefix}header.`),
    cashDeltas: data?.cashDeltas?.map((delta, index) => getCorpactCustomerActionCashDeltaListItems(delta, `${prefix}cashDeltas.${index}.`)),
    securityDeltas: data?.securityDeltas?.map((delta, index) =>
      getCorpactCustomerActionSecurityDeltaListItems(delta, `${prefix}securityDeltas.${index}.`),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactTransactionListItems(data: IxcCorpactionsV1CorpactionTransaction | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    actionId: getReadonlyListItemId(`${prefix}actionId`, 'Action ID', data?.actionId, getCorpactCaAdminActionDetailPath(data?.actionId)),
    snapshotGroup: getReadonlyListItemId(`${prefix}snapshotGroup`, 'Snapshot Group', data?.snapshotGroup),
    balance: getReadonlyListItemNumber(`${prefix}balance`, 'Balance', getNumericStringOrUndefinedFromNullable(data, 'balance')),
    header: getCorpactHeaderListItems(data?.header, `${prefix}header.`),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    assignment: (data?.assignment ? getCorpactCustomerAssignmentListItems(data?.assignment) : undefined) as ReturnType<
      typeof getCorpactCustomerAssignmentListItems
    >,
  } as const satisfies ReadonlyListItemRecord;
}
